// Opt-in codec for the Offices/Leases faceted lists. Legacy consumers retain
// empty-means-all; version 2 records an intentional empty selection.
export const CHECKED_FILTER_VERSION = 2;

export function allOptionValues(dimension) {
  return dimension.options.map(({ value }) => value);
}

export function selectionSetsEqual(a = [], b = []) {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every(value => right.has(value));
}

// Count entities with the other facets applied, then match the option with
// the very same predicate as the list. A record may match overlapping options.
export function withFacetCounts(dimensions, selections, rows, applyFilters) {
  return dimensions.map(dim => ({
    ...dim,
    options: dim.options.map(option => {
      const matches = applyFilters(rows, { ...selections, [dim.id]: [option.value] }, dim.id);
      return { ...option, count: new Set(matches.map(row => row.id)).size };
    }),
  }));
}

// Omit unrestricted groups on write, so async/dynamic options remain all on
// reload. Keep [] explicitly: the version marker distinguishes it from legacy.
export function compactCheckedSelections(dimensions, selections, baseline = {}, explicitEmpty = []) {
  return Object.fromEntries(Object.entries(selections).filter(([id, values]) => {
    const dim = dimensions.find(d => d.id === id);
    if (dim && !dim.options.length && !values.length && !explicitEmpty.includes(id)) return false;
    return !dim || baseline[id] || !values.length || !selectionSetsEqual(values, allOptionValues(dim));
  }));
}

export function normalizeCheckedSelections(dimensions, selections = {}, baseline = {}, {
  legacy = false, emptyMeansNone = [], preserveMissing = [],
} = {}) {
  const next = { ...selections };
  for (const dim of dimensions) {
    const selected = selections[dim.id];
    if (selected === undefined && preserveMissing.includes(dim.id)) continue;
    next[dim.id] = selected === undefined
      ? [...(baseline[dim.id] ?? (dim.restriction ? [] : allOptionValues(dim)))]
      : legacy && !selected.length && !emptyMeansNone.includes(dim.id)
        ? allOptionValues(dim)
        : [...selected];
  }
  return next;
}

// Adapt checked selections to existing predicates, bypassing fully checked
// groups so null/unclassified records remain eligible. None is handled first.
export function checkedPredicateSelections(dimensions, selections, legacyEmpty = false, countGroup = null) {
  if (!legacyEmpty && dimensions.some(({ id, options }) => options.length > 0 && selections[id]?.length === 0)) return null;
  const next = { ...selections };
  for (const dim of dimensions) {
    if (dim.id !== countGroup && dim.options.every(({ value }) => selections[dim.id]?.includes(value))) delete next[dim.id];
  }
  return next;
}
